import React, { useRef, useState } from 'react';
import { Spinner } from 'components/base';
import { getTotalPrice } from 'data/lib/product';
import dynamic from 'next/dynamic';
import { formatData } from 'editor-utils/dataParser';
import { getOriginalPage, getTemplateStyleCategories } from 'editor-utils/template';

import useEditorItemContext from '../../context/editorItemContext';
import EditorLayout from '../components/layout';
import OptionedContinueButton from '../components/optionedContinueButton';
import StylesSection from '../../polotno/section/stylesSection';
import useEditorContext from '../../context/editorContext';
import pagesValidator from '../../lib/pagesValidator';
import Page from '../../components/page/preview';
import SETTINGS from '../../settings';
import { Button } from '../../components/base';

const Polotno = dynamic(() => import('../../polotno'), {
  ssr: false,
  loading: () => <Spinner theme="primary" className="mx-auto" />,
});

const PolotnoPreview = props => {
  const polotnoRef = useRef();

  const editorContext = useEditorContext();
  const { item, product, template, config } = useEditorItemContext();

  const [finalPreview, setFinalPreview] = useState(false);

  const handleOnPreviewClick = () => {
    const { pages } = polotnoRef.current.getData({ photos: item.photos });
    editorContext.setPages(item.id, pages);
    setFinalPreview(true);
  };

  const handleOnSuccessClick = async (checkErrors = true) => {
    const validator = pagesValidator(template, item.pages, config);
    validator.confirmation(
      'I have carefully reviewed my project for errors and agree to print it “as is”. I understand that once I submit my order it will be sent to the printer and cannot be canceled. Because this is a custom product, it is not returnable for credit or refund.',
    );

    const data = formatData(template, item, { validator });

    if (SETTINGS.DEBUG || validator.isValid() || !checkErrors) {
      props.onSuccess({ data });
      return { data };
    }

    const errors = validator.getErrors();
    props.onSuccess({ errors });
    return { errors };
  };

  const handleOnChange = data => {
    editorContext.setPages(item.id, data.pages);
  };

  let polotnoDefaultSection;
  const polotnoCustomSections = [];

  if (getTemplateStyleCategories(template).length > 0) {
    polotnoDefaultSection = 'styles';
    polotnoCustomSections.push(StylesSection);
  }

  if (finalPreview) {
    const type = 'pages';
    const { size } = getOriginalPage(template, item.pages[0], type, type);

    return (
      <EditorLayout
        actionBar={
          <>
            <Button variant="secondary" mr={[1, 1, 2]} onClick={() => setFinalPreview(false)}>
              Go back
            </Button>
          </>
        }
        price={getTotalPrice(product, item.pages.length)}
        actionButton={
          <OptionedContinueButton onClick={handleOnSuccessClick}>
            Add to Cart
          </OptionedContinueButton>
        }
        breadCrumbItems={[{ label: 'Print Preview' }]}
        noPadding
      >
        <div className="w-100 p-3 preview-page-background">
          <Page
            page={{ ...item.pages[0], size, type, visible: true }}
            photos={item.photos}
            template={template}
            fullWidth
            serverSideDPI={72}
            visibleByDefault
            className="preview-page-bordered"
          />
        </div>
      </EditorLayout>
    );
  }

  return (
    <EditorLayout
      actionBar={props.actionBar}
      price={getTotalPrice(product, item.pages.length)}
      actionButton={
        <Button variant="primary" onClick={handleOnPreviewClick}>
          Done
        </Button>
      }
      centeredContent
      noPadding
    >
      {item.pages.length === 1 ? (
        <Polotno
          innerRef={polotnoRef}
          config={config}
          pages={item.pages}
          photos={item.photos}
          additionalInfo={item.additionalInfo}
          template={template}
          spinnerTheme="primary"
          onChange={handleOnChange}
          customSections={polotnoCustomSections}
          defaultSection={polotnoDefaultSection}
        />
      ) : (
        <p className="mx-auto">Eep, wrong number of pages.</p>
      )}
    </EditorLayout>
  );
};

export default PolotnoPreview;
